import { z } from 'zod';
import { PURPOSES, RETENTION_ACTIONS } from './vocabulary.js';
/**
 * Configurazione della retention (§13).
 *
 * I periodi non stanno nel codice e non hanno un default applicativo: sono una
 * decisione del Titolare, che deve poter essere letta, discussa e approvata
 * senza aprire un sorgente. Vivono quindi in un file di configurazione con il
 * riferimento a chi l'ha approvata e quando — quel riferimento non è
 * decorativo, è ciò che rende dimostrabile l'art. 5.2.
 *
 * La validazione è deliberatamente severa: una finalità mancante o duplicata
 * non è un caso da correggere con un default, perché il default sarebbe
 * esattamente la scelta autonoma che §35.10 vieta. Meglio un avvio fallito di
 * una conservazione a tempo indeterminato che nessuno nota.
 */
const policySchema = z.object({
    purpose: z.enum(PURPOSES),
    /**
     * Il periodo decorre dal fatto che genera la finalità: la data della
     * richiesta per (a) e (b), la data in cui il consenso è stato prestato per
     * (c) — mai dalla data di raccolta del contatto, che non dice nulla su
     * quanto a lungo quella specifica finalità resti viva.
     */
    durationDays: z.number().int().positive(),
    action: z.enum(RETENTION_ACTIONS),
    /** Motivazione della scelta: serve al registro dei trattamenti, non al codice. */
    rationale: z.string().min(1).optional(),
});
export const retentionConfigSchema = z
    .object({
    /** Chi ha approvato questi periodi. Un file senza approvazione non è una decisione. */
    approvedBy: z.string().min(1),
    approvedAt: z.string().min(1),
    note: z.string().optional(),
    policies: z.array(policySchema).min(1),
})
    .superRefine((config, ctx) => {
    const seen = new Set();
    for (const policy of config.policies) {
        if (seen.has(policy.purpose)) {
            ctx.addIssue({
                code: 'custom',
                message: `La finalità ${policy.purpose} compare più di una volta: ` +
                    'un periodo di conservazione ambiguo non è una decisione.',
            });
        }
        seen.add(policy.purpose);
    }
    // Una finalità senza periodo configurato significa conservazione senza
    // termine. Deve impedire l'avvio, non passare inosservata.
    const missing = PURPOSES.filter((purpose) => !seen.has(purpose));
    if (missing.length > 0) {
        ctx.addIssue({
            code: 'custom',
            message: `Finalità senza periodo di conservazione: ${missing.join(', ')}. ` +
                'Ogni finalità dichiarata nell’informativa deve avere un termine (§13).',
        });
    }
});
export function buildRetentionConfig(document) {
    const byPurpose = new Map(document.policies.map((policy) => [
        policy.purpose,
        { purpose: policy.purpose, durationDays: policy.durationDays, action: policy.action },
    ]));
    return {
        approvedBy: document.approvedBy,
        approvedAt: document.approvedAt,
        policyFor: (purpose) => {
            const policy = byPurpose.get(purpose);
            if (!policy) {
                // Irraggiungibile con una configurazione validata: resta come rete di
                // sicurezza per il caso in cui si aggiunga una finalità al vocabolario
                // senza aggiornare il file.
                throw new Error(`Nessun periodo di conservazione configurato per ${purpose}.`);
            }
            return policy;
        },
        all: [...byPurpose.values()],
    };
}
//# sourceMappingURL=retention.js.map